import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import CreateWorkspaceDialog from "@/components/CreateWorkspaceDialog";
import MobileTaskCard from "@/components/MobileTaskCard";
import MorningBriefing from "@/components/MorningBriefing";
import CashflowWidget from "@/components/CashflowWidget";
import WalletBalanceWidget from "@/components/WalletBalanceWidget";
import BrandedLoader from "@/components/BrandedLoader";
import { toast } from "sonner";
import { ArrowUpRight, Plus, AlertCircle, Clock, ShoppingCart, Truck, CheckCircle2, XCircle, IndianRupee, Users, Flame, Share2, ShoppingBag, Factory, Copy, Check, QrCode, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { devError } from "@/lib/log";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ordersData, setOrdersData] = useState(null);
  const [error, setError] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  // "my" = only my tasks (default), "team" = admin-only global view.
  const [viewMode, setViewMode] = useState("my");
  // Admin team view filter: null = All, "unassigned" = unassigned, else user id.
  const [assigneeFilter, setAssigneeFilter] = useState(null);

  const load = async (mode = viewMode, assignee = assigneeFilter) => {
    // Progressive load — fire the two dashboard calls in parallel but
    // render each section as soon as its own payload arrives, so users
    // see the tasks pane in ~200 ms even when the orders aggregate is
    // slow. This is the "lazy load in small pieces" pattern for the
    // main Dashboard.
    setError(null);
    try {
      const params = new URLSearchParams({ view: mode });
      if (mode === "team" && assignee) params.set("assignee", assignee);
      const primary = api.get(`/dashboard?${params.toString()}`)
        .then(({ data: d }) => setData(d))
        .catch((e) => {
          devError("dashboard load failed:", e?.message || e);
          setError(e.response?.data?.detail || e.message || "Failed to load dashboard");
        });
      const orders = api.get("/orders/dashboard")
        .then(({ data: od }) => setOrdersData(od))
        .catch(() => setOrdersData(null));
      await Promise.allSettled([primary, orders]);
    } catch (e) {
      devError("dashboard load failed:", e?.message || e);
      setError(e.response?.data?.detail || e.message || "Failed to load dashboard");
    }
  };
  useEffect(() => { load(viewMode, assigneeFilter); }, [viewMode, assigneeFilter]);

  const markTaskDone = async (task) => {
    if (task.status === "done") return;
    try {
      await api.patch(`/tasks/${task.id}`, { status: "done" });
      toast.success("Task marked done");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e, "Failed to update")); }
  };

  const openTaskInWorkspace = (task) => navigate(`/workspace/${task.workspace_id}?task=${task.id}`);

  if (error) {
    return <AppLayout><div className="p-10">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-3">/ error</div>
      <h2 className="font-heading font-black text-3xl mb-3">Could not load dashboard.</h2>
      <p className="text-sm text-[#5C5C5C] mb-6">{String(error)}</p>
      <button onClick={load} data-testid="retry-dashboard-btn" className="border border-[#0D0D0D] bg-[#0D0D0D] text-white px-5 h-11 text-sm font-bold hover:bg-[#FF3B30]">Retry →</button>
    </div></AppLayout>;
  }
  if (!data) {
    return <AppLayout><BrandedLoader label="Loading dashboard" fullscreen={false} /></AppLayout>;
  }

  return (
    <AppLayout>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        <MorningBriefing />
        <WalletBalanceWidget />
        <CashflowWidget />
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] mb-2">/ {greeting()}</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">
              {user?.name ? `Hey, ${user.name.split(" ")[0]}.` : "Command center."}
            </h1>
            {data.overdue > 0 ? (
              <p className="mt-3 text-sm text-[#FF3B30] font-medium flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4" />
                {data.overdue} task{data.overdue > 1 ? "s are" : " is"} overdue — clear the block.
              </p>
            ) : data.due_today > 0 ? (
              <p className="mt-3 text-sm text-[#5C5C5C]">
                <span className="font-bold text-[#0D0D0D]">{data.due_today}</span> due today. Let's ship.
              </p>
            ) : (
              <p className="mt-3 text-sm text-[#5C5C5C]">Nothing urgent. Focus on what moves the needle.</p>
            )}
          </div>
          <button
            onClick={() => setOpenCreate(true)}
            data-testid="new-workspace-btn"
            className="border border-[#0D0D0D] bg-[#FF3B30] text-white px-5 h-11 text-sm font-bold tracking-wide hover:bg-[#0D0D0D] flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> New workspace
          </button>
        </div>

        {/* Workspaces — pinned to the top of the page so they're reachable
            without scrolling (above metrics, orders, tasks and activity). */}
        <div className="mb-8" data-testid="workspaces-section">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] dark:text-white/50 mb-4">/ workspaces</div>
          {data.workspaces.length === 0 ? (
            <div className="border border-dashed border-[#0D0D0D] dark:border-white/20 p-10 text-center bg-white dark:bg-[#111418] rounded-xl">
              <h3 className="font-heading font-bold text-2xl mb-2 text-[#0D0D0D] dark:text-white">No workspaces yet</h3>
              <p className="text-sm text-[#5C5C5C] dark:text-white/60 mb-6">Create your first workspace to start delegating.</p>
              <button onClick={() => setOpenCreate(true)} data-testid="empty-create-workspace-btn"
                className="border border-[#0D0D0D] bg-[#0D0D0D] text-white px-5 h-11 text-sm font-bold hover:bg-[#DC262F] rounded-md">
                Create workspace →
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.workspaces.map((ws) => (
                <Link
                  key={ws.id}
                  to={`/workspace/${ws.id}`}
                  data-testid={`workspace-card-${ws.id}`}
                  className="border border-[#E2E2E2] dark:border-white/10 bg-white dark:bg-[#111418] p-5 card-lift block rounded-xl hover:border-[#DC262F]/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-heading font-bold text-lg truncate text-[#0D0D0D] dark:text-white">{ws.name}</h4>
                    <ArrowUpRight className="h-4 w-4 text-[#5C5C5C] dark:text-white/40" />
                  </div>
                  <p className="text-sm text-[#5C5C5C] dark:text-white/60 line-clamp-2 mb-4 min-h-[2.5rem]">{ws.description || "No description"}</p>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C5C5C] dark:text-white/50 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#DC262F]"></span>
                    {ws.member_ids?.length || 1} member{(ws.member_ids?.length || 1) > 1 ? "s" : ""}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Top metric — My Tasks first (active only, excludes Done), then Due Today (yellow), then Overdue (red) */}
        <div className="grid grid-cols-2 md:grid-cols-4 border border-[#E2E2E2] dark:border-white/10 bg-white dark:bg-[#111418] rounded-xl overflow-hidden shadow-sm mb-8">
          <Metric label="My tasks" value={data.my_tasks_active_total ?? data.my_tasks_total} testid="metric-my-tasks" />
          <Metric label="Due today" value={data.due_today} accent={data.due_today > 0 ? "text-[#B7791F]" : ""} testid="metric-due-today" />
          <Metric label="Overdue" value={data.overdue} accent={data.overdue > 0 ? "text-[#DC262F] text-glow-crimson" : ""} testid="metric-overdue" />
          <Metric label="Workspaces" value={data.total_workspaces} testid="metric-workspaces" border={false} />
        </div>

        {/* Storefront share — admin-only bar with two prebuilt WhatsApp
            messages. B2C combos on the left, B2B wholesale on the right. */}
        {data.is_admin && <StorefrontShareBar />}

        {/* Admin-only global stats + view toggle */}
        {data.is_admin && data.team_stats && (
          <div className="mb-8">
            <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C]">/ factory — team view</div>
              <div className="inline-flex border border-[#0D0D0D] bg-white" data-testid="dashboard-view-toggle">
                <button
                  type="button"
                  data-testid="view-toggle-my"
                  onClick={() => { setViewMode("my"); setAssigneeFilter(null); }}
                  className={`px-4 h-9 text-[11px] font-bold uppercase tracking-widest transition-colors ${viewMode === "my" ? "bg-[#0D0D0D] text-white" : "text-[#0D0D0D] hover:bg-[#F0F0F0]"}`}
                >
                  My view
                </button>
                <button
                  type="button"
                  data-testid="view-toggle-team"
                  onClick={() => setViewMode("team")}
                  className={`px-4 h-9 text-[11px] font-bold uppercase tracking-widest border-l border-[#0D0D0D] transition-colors ${viewMode === "team" ? "bg-[#0D0D0D] text-white" : "text-[#0D0D0D] hover:bg-[#F0F0F0]"}`}
                >
                  Team view
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <AdminMetric
                label="All tasks (active)"
                value={data.team_stats.all_tasks}
                icon={Users}
                tone="neutral"
                testid="metric-all-tasks"
              />
              <AdminMetric
                label="All due today"
                value={data.team_stats.all_due_today}
                icon={Clock}
                tone={data.team_stats.all_due_today > 0 ? "warn" : "neutral"}
                testid="metric-all-due-today"
              />
              <AdminMetric
                label="All overdue"
                value={data.team_stats.all_overdue}
                icon={Flame}
                tone={data.team_stats.all_overdue > 0 ? "danger" : "neutral"}
                pulse={data.team_stats.all_overdue > 0}
                testid="metric-all-overdue"
              />
            </div>

            {/* Team-view assignee filter chips */}
            {viewMode === "team" && (data.team_members?.length > 0) && (
              <div className="mt-4" data-testid="team-filter-chips">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] mb-2">
                  Filter by member
                </div>
                <div className="flex flex-wrap gap-2">
                  <TeamChip
                    label="All"
                    count={data.team_stats.all_tasks}
                    active={!assigneeFilter}
                    color="#0D0D0D"
                    onClick={() => setAssigneeFilter(null)}
                    testid="team-chip-all"
                  />
                  {data.team_members.map((m) => (
                    <TeamChip
                      key={m.id}
                      label={m.name}
                      count={m.total}
                      overdue={m.overdue}
                      color={m.color}
                      active={assigneeFilter === m.id}
                      onClick={() => setAssigneeFilter(m.id)}
                      testid={`team-chip-${m.id}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Order Management summary */}
        {ordersData && (
          <div className="mb-8">
            <div className="flex items-end justify-between mb-3">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] dark:text-white/50">/ orders</div>
              <Link to="/orders" className="text-xs font-bold uppercase tracking-widest text-[#DC262F] hover:underline flex items-center gap-1" data-testid="dashboard-orders-view-all">
                View all <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-[#E2E2E2] dark:border-white/10 bg-white dark:bg-[#111418] rounded-xl overflow-hidden shadow-sm">
              <OrderCard label="Today's orders" value={ordersData.todays_orders} icon={ShoppingCart} testid="metric-todays-orders" />
              <OrderCard label="Pending" value={ordersData.pending_orders} icon={Clock} testid="metric-pending-orders" />
              <OrderCard label="Dispatched" value={ordersData.dispatched_orders} icon={Truck} testid="metric-dispatched-orders" />
              <OrderCard label="Delivered" value={ordersData.delivered_orders} icon={CheckCircle2} testid="metric-delivered-orders" />
              <OrderCard label="Cancelled" value={ordersData.cancelled_orders} icon={XCircle} testid="metric-cancelled-orders" />
              <OrderCard label="Today's sales" value={`₹${Number(ordersData.todays_sales || 0).toFixed(0)}`} icon={IndianRupee} accent="text-emerald-700" border={false} testid="metric-todays-sales" />
            </div>
            {ordersData.recent_orders?.length > 0 && (
              <div className="mt-3 border border-[#E2E2E2] dark:border-white/10 bg-white dark:bg-[#111418] rounded-xl overflow-hidden">
                <div className="px-4 py-2 text-[10px] uppercase tracking-widest font-bold text-neutral-500 dark:text-white/50 border-b border-[#E2E2E2] dark:border-white/10">Recent orders</div>
                <div className="divide-y divide-[#F0F0F0] dark:divide-white/5">
                  {ordersData.recent_orders.slice(0, 5).map((o) => (
                    <Link key={o.id} to={`/orders/${o.id}`} className="flex items-center justify-between px-4 py-2 hover:bg-[#FAFAFA] dark:hover:bg-white/5 text-sm text-[#0D0D0D] dark:text-white/90" data-testid={`dashboard-recent-order-${o.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs font-bold whitespace-nowrap">{o.order_number}</span>
                        <span className="truncate">{o.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs uppercase font-bold tracking-widest text-neutral-500 dark:text-white/60">{o.status}</span>
                        <span className="font-mono font-bold">₹{Number(o.grand_total || 0).toFixed(0)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Tasks (the Workspaces section lives at the top of the page) */}
          <div className="lg:col-span-2">
            {/* My tasks (or Team tasks for admin) */}
            {(() => {
              const showTeam = data.is_admin && viewMode === "team";
              const list = showTeam ? (data.team_tasks || []) : (data.my_tasks || []);
              if (list.length === 0) return null;
              return (
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] dark:text-white/50 mb-4">
                    / {showTeam ? "team queue" : "assigned to me"}
                  </div>
                  {/* Mobile: full-width MobileTaskCard stack (only for personal view; team view uses row list on mobile too) */}
                  {!showTeam && (
                    <div className="md:hidden space-y-3" data-testid="my-tasks-mobile">
                      {list.map((t) => (
                        <MobileTaskCard
                          key={t.id}
                          task={t}
                          assignee={user}
                          onOpen={() => openTaskInWorkspace(t)}
                          onComplete={() => markTaskDone(t)}
                          onAssign={() => openTaskInWorkspace(t)}
                        />
                      ))}
                    </div>
                  )}
                  {/* Desktop / Team-view row list */}
                  <div
                    className={`${showTeam ? "" : "hidden md:block"} border border-[#E2E2E2] dark:border-white/10 bg-white dark:bg-[#111418] divide-y divide-[#E2E2E2] dark:divide-white/5 rounded-xl overflow-hidden`}
                    data-testid={showTeam ? "team-tasks-list" : "my-tasks-desktop"}
                  >
                    {list.map((t) => {
                      const isOverdue = t.due_date && t.due_date.slice(0, 10) < new Date().toISOString().slice(0, 10) && t.status !== "done";
                      return (
                        <Link
                          key={t.id}
                          to={`/workspace/${t.workspace_id}?task=${t.id}`}
                          data-testid={showTeam ? `team-task-${t.id}` : `my-task-${t.id}`}
                          className={`flex items-center gap-3 p-4 hover:bg-[#F7F7F6] dark:hover:bg-white/5 text-[#0D0D0D] dark:text-white/90 ${isOverdue ? "bg-[#FFF5F5] dark:bg-[#DC262F]/10" : ""}`}
                        >
                          <span className={`w-2 h-2 status-${t.status}`}></span>
                          <span className="flex-1 text-sm font-medium truncate">{t.title}</span>
                          {showTeam && t.assignee_name && (
                            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-[#E2E2E2] dark:border-white/15 text-[#5C5C5C] dark:text-white/60 hidden sm:inline-block">
                              {t.assignee_name}
                            </span>
                          )}
                          {t.due_date && (
                            <span className={`text-xs font-mono flex items-center gap-1 ${isOverdue ? "text-[#DC262F] font-bold" : "text-[#5C5C5C] dark:text-white/60"}`}>
                              <Clock className="h-3 w-3" />{t.due_date.slice(0, 10)}
                            </span>
                          )}
                          <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border priority-${t.priority}`}>{t.priority}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Activity */}
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] dark:text-white/50 mb-4">/ activity</div>
            <div className="border border-[#E2E2E2] dark:border-white/10 bg-white dark:bg-[#111418] rounded-xl overflow-hidden">
              {data.recent_activity?.length === 0 ? (
                <div className="p-6 text-sm text-[#5C5C5C] dark:text-white/60 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" /> No recent activity
                </div>
              ) : (
                <ul className="divide-y divide-[#E2E2E2] dark:divide-white/5">
                  {data.recent_activity.map((a) => (
                    <li key={a.id} className="p-4 flex items-start gap-3 text-[#0D0D0D] dark:text-white/90">
                      <span className="w-7 h-7 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ background: a.user_color || "#0D0D0D" }}>
                        {(a.user_name || "U")[0]}
                      </span>
                      <div className="text-sm leading-snug">
                        <span className="font-bold">{a.user_name}</span>{" "}
                        <span className="text-[#5C5C5C] dark:text-white/60">{a.action}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <CreateWorkspaceDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={load} />
    </AppLayout>
  );
}

function Metric({ label, value, accent = "", testid, border = true }) {
  return (
    <div className={`p-6 lg:p-8 ${border ? "border-r border-[#E2E2E2] dark:border-white/10" : ""}`} data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] dark:text-white/50 mb-3">{label}</div>
      <div className={`numeral text-5xl lg:text-6xl font-black tracking-tight text-[#0D0D0D] dark:text-white ${accent}`}>{value}</div>
    </div>
  );
}

/**
 * StorefrontShareBar — twin "Share" pills for the two customer surfaces.
 *   • Retail Shop (B2C combos) → https://<host>/shop
 *   • Business Catalog (B2B)   → https://<host>/catalog
 *
 * Each button copies a curated WhatsApp-ready blurb + URL to the
 * clipboard so admins can paste directly into a WA chat. On modern
 * mobile browsers we also expose the native ``navigator.share`` sheet
 * (Share API) as a one-tap upgrade — the fallback copy path always
 * runs so the operator still gets the message on desktop.
 *
 * Uses the browser's current ``window.location.origin`` at click time
 * so preview shares use the preview host and production uses rimtask.com
 * — no hard-coded domain in the source.
 */
function StorefrontShareBar() {
  const [justCopied, setJustCopied] = useState(null); // "shop" | "catalog"
  // Which tile's QR panel is currently expanded — null / "shop" / "catalog".
  // We only render one QR at a time to keep the dashboard tidy.
  const [qrOpen, setQrOpen] = useState(null);

  const buildMessage = (kind) => {
    const origin = window.location.origin;
    if (kind === "shop") {
      return {
        title: "Rim Plastics · Toy Retail Shop",
        url: `${origin}/shop`,
        text:
          "*Rim Plastics — Retail Shop*\n" +
          "_Safe, Durable & Fun Toys for Ages 3-15_\n\n" +
          "Handpicked toy combos — kid-tested, packed and ready to go.\n" +
          "• Real photos, bold prices, no signup\n" +
          "• Cash-on-Delivery or Razorpay checkout\n" +
          "• Add to cart or WhatsApp us directly\n\n" +
          `Shop now → ${origin}/shop`,
      };
    }
    return {
      title: "Rim Plastics · Business Catalog",
      url: `${origin}/catalog`,
      text:
        "*Rim Plastics — Wholesale Catalog*\n" +
        "_Industrial parts for factories, distributors and OEMs_\n\n" +
        "• 60+ curated SKUs\n" +
        "• Bulk pricing on request — quick WhatsApp confirmation\n\n" +
        `Browse → ${origin}/catalog`,
    };
  };

  /**
   * Fetch the per-route share card (retail vs wholesale) as a File so
   * it can ride along with a navigator.share() call on supporting
   * platforms (Chrome / Safari on mobile). The card is a 1200×630 PNG
   * with a small logo on the left of a white background — no green
   * bleed into WhatsApp chat bubbles, no full-page centered logo.
   * Falls back gracefully to text-only share when the browser either
   * doesn't support file sharing at all or can't share this type.
   */
  const _fetchLogoFile = async (kind = "shop") => {
    const filename = kind === "catalog" ? "share-catalog.png" : "share-shop.png";
    try {
      const resp = await fetch(`${window.location.origin}/${filename}`);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return new File([blob], filename, { type: blob.type || "image/png" });
    } catch { return null; }
  };

  const share = async (kind) => {
    const payload = buildMessage(kind);
    // Try to attach the route-specific share card so it lands on
    // WhatsApp as a branded image + caption (way more brand-forward
    // than a text-only message). Falls back gracefully on browsers
    // that don't support file share.
    const logoFile = await _fetchLogoFile(kind);
    try {
      if (navigator.share) {
        if (logoFile && navigator.canShare && navigator.canShare({ files: [logoFile] })) {
          await navigator.share({ files: [logoFile], title: payload.title, text: payload.text });
        } else {
          await navigator.share({ title: payload.title, text: payload.text, url: payload.url });
        }
        return;
      }
    } catch (_err) { /* user cancelled or share failed — proceed to copy */ }

    try {
      await navigator.clipboard.writeText(payload.text);
      setJustCopied(kind);
      toast.success(
        kind === "shop" ? "Retail shop share message copied" : "Business catalog share message copied",
      );
      // Auto-revert the tick indicator so a second copy still feels responsive.
      setTimeout(() => setJustCopied((cur) => (cur === kind ? null : cur)), 1800);
    } catch {
      toast.error("Could not copy — please try again");
    }
  };

  return (
    <div className="mb-8" data-testid="dashboard-storefront-share">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Share2 className="h-4 w-4 text-[#5C5C5C] dark:text-white/50" />
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] dark:text-white/50">
            / share your storefronts
          </div>
        </div>
        <div className="text-[11px] text-[#5C5C5C] dark:text-white/50">
          One tap · WhatsApp ready
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ShareTile
          kind="shop"
          icon={ShoppingBag}
          accent="#FF3B30"
          title="Share B2C Shop"
          subtitle="Toys → retail shop"
          url="/shop"
          onShare={() => share("shop")}
          copied={justCopied === "shop"}
          qrOpen={qrOpen === "shop"}
          onToggleQR={() => setQrOpen((c) => (c === "shop" ? null : "shop"))}
        />
        <ShareTile
          kind="catalog"
          icon={Factory}
          accent="#0D0D0D"
          title="Share B2B Catalog"
          subtitle="Industrial parts → for wholesale"
          url="/catalog"
          onShare={() => share("catalog")}
          copied={justCopied === "catalog"}
          qrOpen={qrOpen === "catalog"}
          onToggleQR={() => setQrOpen((c) => (c === "catalog" ? null : "catalog"))}
        />
      </div>
    </div>
  );
}

function ShareTile({ kind, icon: Icon, accent, title, subtitle, url, onShare, copied, qrOpen, onToggleQR }) {
  const absoluteUrl = `${window.location.origin}${url}`;

  // Rasterise the inline SVG QR into a PNG and offer it as a file
  // download — perfect for printing on shop counters, delivery slips,
  // or business cards. Renders at 4× the on-screen size so the PNG is
  // crisp when scaled up in print.
  const downloadQR = (e) => {
    e.stopPropagation();
    const svg = document.querySelector(`[data-testid="share-${kind}-qr-svg"]`);
    if (!svg) return;
    const size = 720; // print-friendly resolution
    const clone = svg.cloneNode(true);
    clone.setAttribute("width", size);
    clone.setAttribute("height", size);
    const src = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([src], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(svgUrl);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `rim-${kind}-qr.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      }, "image/png");
    };
    img.src = svgUrl;
  };

  return (
    <div className="border border-[#E2E2E2] dark:border-white/10 bg-white dark:bg-[#111418] rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <button
        type="button"
        onClick={onShare}
        data-testid={`share-${kind}-btn`}
        className="group flex items-center gap-4 p-4 md:p-5 text-left w-full hover:-translate-y-0.5 transition-transform duration-200"
      >
        <div
          className="w-11 h-11 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 text-white transition-transform duration-300 group-hover:scale-105"
          style={{ backgroundColor: accent }}
          aria-hidden="true"
        >
          <Icon className="h-5 w-5 md:h-6 md:w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-black tracking-tight text-sm md:text-base text-[#0D0D0D] dark:text-white truncate">
            {title}
          </div>
          <div className="text-[11px] text-[#5C5C5C] dark:text-white/60 truncate">{subtitle}</div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-400 dark:text-white/40 truncate">
            {window.location.host}{url}
          </div>
        </div>
        <div
          className="flex items-center gap-1 px-3 h-9 rounded-full text-[10px] font-bold uppercase tracking-widest flex-shrink-0 transition-colors"
          style={{
            backgroundColor: copied ? "#059669" : accent,
            color: "#FFFFFF",
          }}
          data-testid={`share-${kind}-status`}
        >
          {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
        </div>
      </button>

      {/* QR toggle strip — always present so admins learn the feature. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleQR(); }}
        data-testid={`share-${kind}-qr-toggle`}
        aria-expanded={qrOpen}
        className="w-full flex items-center justify-center gap-1.5 px-4 py-2 text-[10px] font-bold uppercase tracking-widest border-t border-[#E2E2E2] dark:border-white/10 text-[#5C5C5C] dark:text-white/60 hover:bg-[#FAFAFA] dark:hover:bg-white/5 transition-colors"
      >
        <QrCode className="h-3 w-3" />
        {qrOpen ? "Hide QR code" : "Show QR code"}
      </button>

      {qrOpen && (
        <div
          className="px-4 py-5 border-t border-[#E2E2E2] dark:border-white/10 bg-[#FAFAFA] dark:bg-[#0B0D10] flex flex-col items-center gap-3"
          data-testid={`share-${kind}-qr-panel`}
        >
          <div className="p-3 bg-white rounded-lg shadow-sm ring-1 ring-[#E2E2E2]">
            <QRCodeSVG
              value={absoluteUrl}
              size={160}
              level="M"
              includeMargin={false}
              fgColor={accent}
              bgColor="#FFFFFF"
              data-testid={`share-${kind}-qr-svg`}
            />
          </div>
          <div className="text-center">
            <div className="text-[11px] font-bold text-[#0D0D0D] dark:text-white">
              Scan to open {kind === "shop" ? "the retail shop" : "the wholesale catalog"}
            </div>
            <div className="mt-0.5 font-mono text-[10px] text-neutral-400 dark:text-white/40 break-all">
              {absoluteUrl}
            </div>
          </div>
          <button
            type="button"
            onClick={downloadQR}
            data-testid={`share-${kind}-qr-download`}
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-[10px] font-bold uppercase tracking-widest text-white transition-colors"
            style={{ backgroundColor: accent }}
          >
            <Download className="h-3 w-3" /> Download PNG
          </button>
        </div>
      )}
    </div>
  );
}

// Admin-only, high-contrast global stat card. Premium Industrial styling:
// rounded corners, soft shadow, thin crimson border. `danger` tone adds a
// gentle crimson pulse so factory managers can spot it from across the room.
function AdminMetric({ label, value, icon: Icon, tone = "neutral", pulse = false, testid }) {
  const tones = {
    neutral: {
      wrap: "bg-[#0B0D10] text-white",
      label: "text-white/55",
      icon: "text-white/70",
      value: "text-white",
    },
    warn: {
      wrap: "bg-gradient-to-br from-[#8A5A0A] to-[#B7791F] text-white",
      label: "text-white/75",
      icon: "text-white/85",
      value: "text-white",
    },
    danger: {
      wrap: "bg-gradient-to-br from-[#8B0F1A] via-[#B01522] to-[#DC262F] text-white",
      label: "text-white/80",
      icon: "text-white",
      value: "text-white text-glow-crimson",
    },
  };
  const c = tones[tone] || tones.neutral;
  return (
    <div
      data-testid={testid}
      className={`card-premium relative p-6 flex items-start justify-between gap-4 ${c.wrap} ${pulse ? "pulse-crimson" : ""}`}
    >
      <div>
        <div className={`font-mono text-[10px] uppercase tracking-[0.3em] mb-3 ${c.label}`}>{label}</div>
        <div className={`numeral text-5xl lg:text-6xl font-black leading-none tracking-tight ${c.value}`}>{value}</div>
      </div>
      {Icon && <Icon className={`h-6 w-6 ${c.icon}`} />}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "late night";
  if (h < 12) return "good morning";
  if (h < 17) return "good afternoon";
  if (h < 21) return "good evening";
  return "good night";
}

function OrderCard({ label, value, icon: Icon, accent = "", border = true, testid }) {
  return (
    <div className={`p-4 ${border ? "border-r border-[#E2E2E2] dark:border-white/10" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C5C5C] dark:text-white/50">{label}</div>
        {Icon && <Icon className="h-3.5 w-3.5 text-neutral-400 dark:text-white/40" />}
      </div>
      <div className={`text-2xl font-black tracking-tight text-[#0D0D0D] dark:text-white ${accent}`}>{value}</div>
    </div>
  );
}

// Compact assignee chip for the admin Team View filter row. Shows the member's
// name + active-task count, with an inline overdue pill when they're behind.
function TeamChip({ label, count, overdue = 0, color = "#0D0D0D", active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 h-8 border rounded-full transition-colors ${active
        ? "bg-[#DC262F] text-white border-[#DC262F] shadow-[0_0_16px_-4px_rgba(220,38,47,0.6)]"
        : "bg-white dark:bg-white/[0.03] text-[#0D0D0D] dark:text-white/85 border-[#E2E2E2] dark:border-white/10 hover:border-[#DC262F] dark:hover:border-[#DC262F]/70"}`}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{ background: color }}
        aria-hidden="true"
      />
      <span className="text-[12px] font-bold truncate max-w-[140px]">{label}</span>
      <span className={`text-[10px] font-mono ${active ? "text-white/80" : "text-[#5C5C5C] dark:text-white/50"}`}>{count}</span>
      {overdue > 0 && (
        <span className={`text-[10px] font-mono font-bold px-1.5 rounded-sm ${active ? "bg-white/25 text-white" : "bg-[#FFE5E3] dark:bg-[#DC262F]/25 text-[#DC262F] dark:text-[#FF6D66]"}`}>
          {overdue}!
        </span>
      )}
    </button>
  );
}
